import axios from 'axios';
import { Api } from '../axiosApi';

export type TSituacaoNotaCompra = 'normal' | 'cancelada' | 'denegada' | 'irregular';

export interface INotaCompra {
  // Preenchido quando o XML completo já chegou (notasnfe); sem ele, só há o resumo.
  idnfe: string | null;
  idresnfe: string | null;
  chave: string;
  numero: string;
  nome: string;
  cnpj: string;
  dhemi: string;
  valor: string | number;
  lancada: boolean;
  // Evento de manifestação do resumo ('000000' = ainda sem manifestação).
  manifestacao: string | null;
  situacao: TSituacaoNotaCompra | null;
  xmotivo: string | null;
  dhconsulta: string | null;
  // Nunca consultada, ou "normal" consultada há mais de 24h.
  verificar: boolean;
}

export interface IResumoNotasCompra {
  total: number;
  normais: number;
  canceladas: number;
  denegadas: number;
  irregulares: number;
  naoverificadas: number;
  // Não verificadas e emitidas há mais de 180 dias: a SEFAZ não consulta mais.
  foradoprazo: number;
  // Válidas com o XML completo: são as que entram no ZIP.
  validascomxml: number;
  valorvalidas: number;
  // Chaves que a verificação do mês precisa consultar na SEFAZ.
  pendentes: string[];
}

export interface IFiltroNotasCompra {
  pagina: number;
  limite: number;
  busca: string;
  situacao: string;
  dataInicial: string;
  dataFinal: string;
}

const mensagemDeErro = async (error: unknown, padrao: string): Promise<Error> => {
  if (axios.isAxiosError(error) && error.response) {
    let corpo = error.response.data;
    // Download pede resposta em blob, inclusive quando vem erro em JSON.
    if (corpo instanceof Blob) {
      try {
        corpo = JSON.parse(await corpo.text());
      } catch {
        corpo = null;
      }
    }
    const erros = corpo?.errors;
    return new Error((typeof erros === 'string' ? erros : erros?.default) || padrao);
  }
  if (axios.isAxiosError(error) && error.request) return new Error('Nenhuma resposta do servidor');
  return new Error(padrao);
};

const getAll = async ({ pagina, limite, busca, situacao, dataInicial, dataFinal }: IFiltroNotasCompra): Promise<{ data: INotaCompra[]; totalCount: number } | Error> => {
  try {
    const { data, headers } = await Api.get<INotaCompra[]>('/notascompra', {
      params: { page: pagina, limit: limite, filter: busca, situacao, datainicial: dataInicial, datafinal: dataFinal },
    });
    return { data, totalCount: Number(headers['x-total-count'] || 0) };
  } catch (error) {
    return mensagemDeErro(error, 'Erro ao listar as notas de compra.');
  }
};

const consultarSituacao = async (chave: string): Promise<{ situacao: TSituacaoNotaCompra; xmotivo: string; dhconsulta: string } | Error> => {
  try {
    const { data } = await Api.get(`/notascompra/situacao/${chave}`, { timeout: 45000 });
    return data;
  } catch (error) {
    const erro = await mensagemDeErro(error, 'Não foi possível consultar a situação da nota.');
    // 429 = SEFAZ bloqueou/fora do ar; sem resposta = servidor inalcançável. Nos
    // dois casos a verificação em lote precisa parar em vez de seguir nota a nota.
    if (axios.isAxiosError(error) && (error.response?.status === 429 || !error.response)) erro.name = 'BloqueioSefaz';
    return erro;
  }
};

// mes no formato AAAA-MM (mês de emissão).
const resumoDoMes = async (mes: string): Promise<IResumoNotasCompra | Error> => {
  try {
    const { data } = await Api.get<IResumoNotasCompra>(`/notascompra/resumo/${mes}`);
    return data;
  } catch (error) {
    return mensagemDeErro(error, 'Erro ao consultar o resumo do mês.');
  }
};

const baixarBlob = async (url: string, nomeArquivo: string, padrao: string): Promise<void | Error> => {
  try {
    const { data } = await Api.get(url, { responseType: 'blob', timeout: 120000 });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(data);
    link.download = nomeArquivo;
    link.click();
    URL.revokeObjectURL(link.href);
  } catch (error) {
    return mensagemDeErro(error, padrao);
  }
};

const baixarXml = (nota: INotaCompra) =>
  baixarBlob(`/notascompra/${nota.idnfe}/xml`, `${nota.chave}.xml`, 'Erro ao baixar o XML da nota.');

const baixarDanfe = (nota: INotaCompra) =>
  baixarBlob(`/danfeentrada/${nota.idnfe}`, `DANFE-${nota.chave}.pdf`, 'Erro ao gerar o DANFE da nota.');

const baixarValidasDoMes = (mes: string) =>
  baixarBlob(`/notascompra/zipvalidas/${mes}`, `notas-validas-${mes}.zip`, 'Erro ao baixar as notas válidas do mês.');

export const NotasCompraService = {
  getAll,
  consultarSituacao,
  resumoDoMes,
  baixarXml,
  baixarDanfe,
  baixarValidasDoMes,
};
